import type { ReactNode } from "react";
import { CheckForApplicationUpdate } from "react-admin";
import { SolarLayout } from "@react-admin/ra-navigation";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ThemeProvider } from "@mui/material/styles";

import { SidebarMenu } from "./sidebar/SidebarMenu";
import CustomBreadcrumb from "./Breadcrumb";
import NotificationListener from "./NotificationListener";
import { Box } from "@mui/material";
import { ClarityConsentBanner } from "./ClarityConsentBanner";
import { ClarityResourceTracker } from "./ClarityResourceTracker";
import { useAppTheme } from "@/hooks/useAppTheme";
import { PartnerContextBar } from "./PartnerContextBar";
import { ApiVersionIndicator } from "./ApiVersionIndicator";
import { HelpButton } from "./HelpButton";
import { SuperAdminContextBanner } from "./SuperAdminContextBanner";
import { PartnerSwitcherDialog } from "../partner/PartnerSwitcherMenuItem";
import { CascadeDeleteProvider } from "@/resources/documents/CascadeDeleteProvider";

const PartnerThemeWrapper = ({ children }: { children: ReactNode }) => {
  const { theme, hasPartnerBranding } = useAppTheme();

  if (!hasPartnerBranding) return <>{children}</>;

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export const Layout = ({ children }: { children: ReactNode }) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <PartnerThemeWrapper>
      <SolarLayout
        menu={SidebarMenu}
        sx={{
          "& .RaSolarLayout-content": {
            marginTop: 0,
            padding: 0,
          },
        }}
      >
        <SuperAdminContextBanner />
        <Box
          sx={{
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            minHeight: 36,
            pr: 2,
          }}
        >
          <CustomBreadcrumb />
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
            <PartnerContextBar />
            <ApiVersionIndicator />
            <HelpButton />
          </Box>
        </Box>
        {/* CascadeDeleteProvider wraps the routed content so a background document
            cascade-delete (started from a dialog that then closes) keeps polling and
            reporting progress even after that dialog unmounts (#6141). */}
        <CascadeDeleteProvider>{children}</CascadeDeleteProvider>
        <CheckForApplicationUpdate interval={60000} fetchOptions={{ cache: "no-cache" }} url="/version.json" />
        <NotificationListener />
        <PartnerSwitcherDialog />
      </SolarLayout>
    </PartnerThemeWrapper>
    <ClarityResourceTracker />
    <ClarityConsentBanner />
  </LocalizationProvider>
);
